/**
 * Frontend mirror of backend CommissionStatementStatus (cn/enums/CommissionStatementStatus.java).
 *
 * Ordinal order MUST match the backend declaration exactly — the column is persisted
 * @Enumerated(ORDINAL), so the ordinal number is what the DB stores and what the entity CRUD
 * DTO returns. Append-only: new values may only be added at the end; existing ordinals must
 * never be reordered or removed.
 *
 *   NEW           → ordinal 0  — Abrechnung empfangen, noch nicht verarbeitet
 *   PROCESSING    → ordinal 1  — Abrechnung wird gerade verarbeitet
 *   RECONCILED    → ordinal 2  — Abrechnung mit Soll-Werten abgeglichen
 *   CLARIFICATION → ordinal 3  — Abrechnung in Klärung
 *   CLOSED        → ordinal 4  — Abrechnung abgeschlossen
 *   ERROR         → ordinal 5  — Abrechnung mit Fehler
 */
export enum CommissionStatementStatusEnum {
    NEW,
    PROCESSING,
    RECONCILED,
    CLARIFICATION,
    CLOSED,
    ERROR,
}

/**
 * German UI labels for CommissionStatementStatusEnum values.
 * Keyed by ordinal so label resolution is O(1) without string→enum conversion.
 */
export const CommissionStatementStatusLabels: Record<CommissionStatementStatusEnum, string> = {
    [CommissionStatementStatusEnum.NEW]: 'Neu',
    [CommissionStatementStatusEnum.PROCESSING]: 'In Verarbeitung',
    [CommissionStatementStatusEnum.RECONCILED]: 'Abgeglichen',
    [CommissionStatementStatusEnum.CLARIFICATION]: 'In Klärung',
    [CommissionStatementStatusEnum.CLOSED]: 'Abgeschlossen',
    [CommissionStatementStatusEnum.ERROR]: 'Fehler',
};

/**
 * Resolve a CommissionStatement `status` value coming from EITHER source into its ordinal:
 *   - the rich import detail endpoint (GET /cn/import/{id}) returns the enum NAME string (e.g. "NEW"),
 *   - the entity CRUD endpoint (GET /cn/commissionStatements/{id}) returns the persisted ORDINAL number.
 *
 * Returns the matching CommissionStatementStatusEnum ordinal, or undefined when the value is
 * null/undefined or does not resolve to a known enum constant.
 */
export function commissionStatementStatusOrdinal(
    status: string | number | null | undefined
): CommissionStatementStatusEnum | undefined {
    if (status === null || status === undefined || status === '') {
        return undefined;
    }

    let ordinal: number | undefined;

    if (typeof status === 'number') {
        // CRUD entity DTO → persisted ordinal.
        ordinal = status;
    } else {
        const trimmed = status.trim();
        if (/^\d+$/.test(trimmed)) {
            // Numeric string (e.g. "0") — treat as ordinal.
            ordinal = Number(trimmed);
        } else {
            // Enum NAME string (e.g. "NEW") — look up the ordinal by name.
            const named = CommissionStatementStatusEnum[trimmed as keyof typeof CommissionStatementStatusEnum];
            if (typeof named === 'number') {
                ordinal = named;
            }
        }
    }

    if (ordinal === undefined || !(ordinal in CommissionStatementStatusLabels)) {
        return undefined;
    }
    return ordinal as CommissionStatementStatusEnum;
}

/**
 * Resolve a CommissionStatement `status` value (enum NAME string OR persisted ordinal number) into
 * its German display label. Returns '–' for null/unknown values.
 */
export function commissionStatementStatusLabel(status: string | number | null | undefined): string {
    const ordinal = commissionStatementStatusOrdinal(status);
    return ordinal === undefined ? '–' : CommissionStatementStatusLabels[ordinal];
}

/**
 * Resolve a CommissionStatement `status` value into its canonical English enum NAME (e.g. "NEW").
 * Used to feed the shared {@link cnStatusSeverity} map (which keys on the English name), so badge
 * severity keeps resolving regardless of whether the source delivered a name or an ordinal.
 * Returns undefined for null/unknown values.
 */
export function commissionStatementStatusName(
    status: string | number | null | undefined
): string | undefined {
    const ordinal = commissionStatementStatusOrdinal(status);
    return ordinal === undefined ? undefined : CommissionStatementStatusEnum[ordinal];
}
